/* @jsx h */
import { computed, defineComponent, h, ref, VNode } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";

export type AddMemberInput = {
  email: string;
  name: string | null;
  phone: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  birthday: string | null;
};

export type AddMemberModalProps = {
  onClose: () => void;
  onSubmit: (input: AddMemberInput) => void;
  /** Mirrors the add mutation, so the form shows progress and server errors. */
  pending?: boolean;
  error?: string | null;
};

export const AddMemberModal = defineComponent<AddMemberModalProps>({
  name: "AddMemberModal",
  props: ["onClose", "onSubmit", "pending", "error"],
  setup(props) {
    const { t } = useLocalization();

    const name = ref("");
    const email = ref("");
    const phone = ref("");
    const birthday = ref("");

    const emailValid = computed(() =>
      /^\S+@\S+\.\S+$/.test(email.value.trim()),
    );

    const submit = () => {
      if (!emailValid.value || props.pending) return;
      props.onSubmit({
        email: email.value.trim(),
        name: name.value.trim() || null,
        phone: phone.value.trim() || null,
        birthday: birthday.value || null,
      });
    };

    const field = (label: string, control: VNode, required = false) => (
      <label class="form-control w-full">
        <span class="label-text mb-1 text-xs font-medium text-base-content/60">
          {label}
          {required ? <span class="text-error"> *</span> : null}
        </span>
        {control}
      </label>
    );

    const inputClass =
      "input input-bordered input-sm w-full focus:input-primary";

    return () => (
      <LemmonModal
        title={t("group.loyalty.add_member")}
        onClose={props.onClose}
        style="max-width: 420px;"
        renderButtons={() => (
          <div class="flex items-center justify-end gap-2 border-t border-base-200 p-3">
            <button
              class="btn btn-ghost btn-sm"
              onClick={() => props.onClose()}
            >
              {t("general.words.cancel")}
            </button>
            <button
              class="btn btn-primary btn-sm gap-1"
              disabled={!emailValid.value || props.pending}
              onClick={submit}
            >
              {props.pending ? (
                <span class="loading loading-spinner loading-xs" />
              ) : (
                <span class="material-symbols-outlined text-[16px]">add</span>
              )}
              {t("group.loyalty.add")}
            </button>
          </div>
        )}
      >
        <div class="flex flex-col gap-3 p-4">
          {field(
            t("group.loyalty.field_name"),
            <input
              class={inputClass}
              type="text"
              value={name.value}
              onInput={(e) =>
                (name.value = (e.target as HTMLInputElement).value)
              }
            />,
          )}
          {field(
            t("group.loyalty.field_email"),
            <input
              class={inputClass}
              type="email"
              placeholder={t("group.loyalty.email_placeholder")}
              value={email.value}
              onInput={(e) =>
                (email.value = (e.target as HTMLInputElement).value)
              }
              onKeydown={(e: KeyboardEvent) => e.key === "Enter" && submit()}
            />,
            true,
          )}
          {field(
            t("group.loyalty.field_phone"),
            <input
              class={inputClass}
              type="tel"
              value={phone.value}
              onInput={(e) =>
                (phone.value = (e.target as HTMLInputElement).value)
              }
            />,
          )}
          {field(
            t("group.loyalty.field_birthday"),
            <input
              class={inputClass}
              type="date"
              value={birthday.value}
              onInput={(e) =>
                (birthday.value = (e.target as HTMLInputElement).value)
              }
            />,
          )}

          <p class="text-xs text-base-content/40">
            {t("group.loyalty.add_member_hint")}
          </p>

          {props.error ? (
            <div class="text-xs text-error">{props.error}</div>
          ) : null}
        </div>
      </LemmonModal>
    );
  },
});
